import { resolvePieveInteractionSource, toSafeLowerString, toSafeObjectOrNull as toObject, toSafeString } from '@shared/utils';
import normalizeConnectionSetupPayload from '@/lib/connections/normalizeConnectionSetupPayload.js';
import getInteractionEventId from '@/lib/threadInteractions/getInteractionEventId.js';
import getInteractionRunId from '@/lib/threadInteractions/getInteractionRunId.js';

const WORKFLOW_ACTION_NEEDED_INTERACTION_TYPE = 'workflow_action_needed';

const normalizeInteractionOptions = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        const label = toSafeString(item);

        if (!label) {
          return null;
        }

        return {
          id: `option-${index + 1}`,
          label,
          value: label
        };
      }

      const optionId = toSafeString(item?.id || item?.key || item?.value || `option-${index + 1}`);
      const optionLabel = toSafeString(item?.label || item?.name || item?.title || optionId);

      if (!optionLabel) {
        return null;
      }

      return {
        id: optionId || `option-${index + 1}`,
        label: optionLabel,
        value: item?.value !== undefined ? item.value : optionId
      };
    })
    .filter(Boolean);
};

const getInteractionPayloadEnvelope = (event = {}) => {
  const payload = toObject(event?.payload) || {};
  const interactionPayload = toObject(payload?.interaction) || {};
  const interactionDataPayload = toObject(interactionPayload?.payload) || {};

  return {
    payload,
    interactionPayload,
    interactionDataPayload
  };
};

const hasConnectionSetupPayloadShape = (value) => {
  const source = toObject(value);

  if (!source) {
    return false;
  }

  if (toObject(source?.app) || toObject(source?.setup)) {
    return true;
  }

  if (Array.isArray(source?.candidates)) {
    return true;
  }

  if (toObject(source?.selectedConnection) || toObject(source?.targetConnection)) {
    return true;
  }

  return false;
};

const resolveConnectionSetupPayloadSource = ({ payload, interactionPayload, interactionDataPayload }) => {
  const candidatePayloads = [interactionDataPayload, interactionDataPayload?.connectionSetup, interactionDataPayload?.connection_setup, interactionPayload, interactionPayload?.connectionSetup, interactionPayload?.connection_setup, payload?.connectionSetup, payload?.connection_setup, payload];

  for (const candidate of candidatePayloads) {
    if (hasConnectionSetupPayloadShape(candidate)) {
      return candidate;
    }
  }

  return payload;
};

const normalizeInteractionQuestions = ({ event, interactionId }) => {
  const { payload, interactionPayload, interactionDataPayload } = getInteractionPayloadEnvelope(event);
  const payloadQuestions = Array.isArray(payload?.questions) ? payload.questions : [];
  const interactionPayloadQuestions = Array.isArray(interactionPayload?.questions) ? interactionPayload.questions : [];
  const interactionDataQuestions = Array.isArray(interactionDataPayload?.questions) ? interactionDataPayload.questions : [];
  const eventQuestions = Array.isArray(event?.questions) ? event.questions : [];
  let sourceQuestions = payloadQuestions;

  if (interactionPayloadQuestions.length > 0) {
    sourceQuestions = interactionPayloadQuestions;
  }

  if (interactionDataQuestions.length > 0) {
    sourceQuestions = interactionDataQuestions;
  }

  if (sourceQuestions.length < 1) {
    sourceQuestions = eventQuestions;
  }

  const normalizedQuestions = sourceQuestions
    .map((question, index) => {
      const questionObject = toObject(question) || {};
      const questionId = toSafeString(questionObject?.id || questionObject?.key || `${interactionId}:q${index + 1}`);
      const questionPrompt = toSafeString(questionObject?.question || questionObject?.prompt || questionObject?.title || questionObject?.label || questionObject?.detail || questionObject?.description);
      const questionDetail = toSafeString(questionObject?.detail || questionObject?.description || questionObject?.helpText || '');

      if (!questionPrompt) {
        return null;
      }

      return {
        id: questionId || `${interactionId}:q${index + 1}`,
        prompt: questionPrompt,
        detail: questionDetail || null,
        options: normalizeInteractionOptions(questionObject?.options)
      };
    })
    .filter(Boolean);

  if (normalizedQuestions.length > 0) {
    return normalizedQuestions;
  }

  const fallbackPrompt = toSafeString(event?.prompt || event?.title || event?.question || interactionPayload?.prompt || interactionPayload?.title || interactionPayload?.question || interactionDataPayload?.prompt || interactionDataPayload?.title || interactionDataPayload?.question || payload?.prompt || payload?.title || payload?.question || event?.detail || interactionPayload?.detail || interactionDataPayload?.detail || payload?.detail || 'Respond to continue');
  const fallbackDetail = toSafeString(event?.detail || event?.description || interactionPayload?.detail || interactionPayload?.description || interactionDataPayload?.detail || interactionDataPayload?.description || payload?.detail || payload?.description || '');
  const fallbackOptions = normalizeInteractionOptions(event?.options || interactionPayload?.options || interactionDataPayload?.options || payload?.options);

  return [
    {
      id: `${interactionId}:q1`,
      prompt: fallbackPrompt || 'Respond to continue',
      detail: fallbackDetail || null,
      options: fallbackOptions
    }
  ];
};

const normalizePendingInteraction = (event = {}) => {
  const normalizedType = toSafeLowerString(event?.type);
  const interactionId = getInteractionEventId(event);
  const interactionRunId = getInteractionRunId(event);

  if (!interactionId) {
    return null;
  }

  const interactionSource = resolvePieveInteractionSource({
    interactionId,
    interactionSource: event?.interactionSource || event?.payload?.interactionSource
  });

  if (normalizedType === 'approval') {
    return {
      mode: 'approval',
      interactionId,
      interactionSource,
      runId: interactionRunId,
      interactionType: toSafeLowerString(event?.interactionType || 'permission_approval') || 'permission_approval',
      targetMode: toSafeLowerString(event?.targetMode) || null,
      title: toSafeString(event?.title || event?.prompt || 'Approve action') || 'Approve action',
      detail: toSafeString(event?.detail || ''),
      denyLabel: toSafeString(event?.denyLabel || 'No') || 'No',
      approveOnceLabel: toSafeString(event?.approveOnceLabel || 'Yes') || 'Yes',
      approveRememberLabel: toSafeString(event?.approveRememberLabel || "Yes and don't ask again") || "Yes and don't ask again"
    };
  }

  if (normalizedType !== 'interaction') {
    return null;
  }

  const { payload, interactionPayload, interactionDataPayload } = getInteractionPayloadEnvelope(event);
  const interactionType = toSafeLowerString(event?.interactionType || interactionPayload?.interactionType || interactionDataPayload?.interactionType || payload?.interactionType || event?.kind || interactionPayload?.kind || interactionDataPayload?.kind || payload?.kind || 'generic_prompt') || 'generic_prompt';

  /* An action card is a filed to-do, not a prompt the run waits on: the whole point is that Pieve keeps
     building while a person clears it. Returning null here is what makes it non-blocking everywhere at
     once — the sidebar's pending dot, the composer's input-required state, and every other consumer read
     the same helper. */
  if (interactionType === WORKFLOW_ACTION_NEEDED_INTERACTION_TYPE) {
    return null;
  }
  const resolvedConnectionSetupPayload = resolveConnectionSetupPayloadSource({
    payload,
    interactionPayload,
    interactionDataPayload
  });
  const isConnectionInteraction = interactionType === 'connection_setup' || hasConnectionSetupPayloadShape(resolvedConnectionSetupPayload);
  const normalizedConnectionSetup = isConnectionInteraction ? normalizeConnectionSetupPayload(resolvedConnectionSetupPayload) : null;
  const title = toSafeString(event?.title || event?.prompt || interactionPayload?.title || interactionPayload?.prompt || interactionDataPayload?.title || interactionDataPayload?.prompt || payload?.title || payload?.prompt || 'Respond to continue') || 'Respond to continue';
  const detail = toSafeString(event?.detail || interactionPayload?.detail || interactionDataPayload?.detail || payload?.detail || '');
  const questions = normalizeInteractionQuestions({
    event,
    interactionId
  });
  const actionLabel = toSafeString(event?.actionLabel || interactionPayload?.actionLabel || interactionDataPayload?.actionLabel || payload?.actionLabel || '');

  return {
    mode: isConnectionInteraction ? 'connection' : 'question',
    interactionId,
    interactionSource,
    runId: interactionRunId,
    interactionType,
    title,
    detail,
    actionLabel: actionLabel || null,
    questions,
    connectionSetup: normalizedConnectionSetup
  };
};

export default normalizePendingInteraction;
